import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { FileTileModel } from '@/features/ai-agents/shared/models';
import { useMediaApi } from '@/webapi/use-media-api';

const MAX_FILES = 3;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

export function useChatFilesUpload() {
  const [filesToUpload, setFilesToUpload] = React.useState<File[]>([]);
  const [uploadedImages, setUploadedImages] = React.useState<
    Array<string | string[]>
  >([]);
  const [uploadedFilesRef, setUploadedFilesRef] = useState<File[]>([]);
  const [isUploading, setIsUploading, isUploadingRef] = useDetachedState(false);

  const { uploadGenericImage, uploadGenericVideo } = useMediaApi();
  const addFileToUploadQueue = (file: File) => {
    const isVideo = file.type.startsWith(`video/`);
    const isImage = file.type.startsWith(`image/`);
    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      toast(`Video file too large: Please upload a video no larger than 50MB`, {
        theme: `colored`,
        type: `error`,
      });
      return;
    }
    if (isImage && file.size > MAX_IMAGE_SIZE) {
      toast(`Image file too large: Please upload an image no larger than 5MB`, {
        theme: `colored`,
        type: `error`,
      });
      return;
    }
    setFilesToUpload((prevFiles) => [...prevFiles, file]);
  };

  const dropzoneProps = useDropzone({
    maxFiles: MAX_FILES,
    accept: `image/*`,
    onDropAccepted: (acceptedFiles) => {
      acceptedFiles.forEach((file) => addFileToUploadQueue(file));
    },
    onDropRejected: (_) => {
      toast(`File upload failed: Please upload an image or video file`, {
        theme: `colored`,
        type: `error`,
      });
    },
  });

  useEffect(() => {
    if (filesToUpload.length > 0 && !isUploadingRef?.current) {
      const current = filesToUpload[0];
      setIsUploading(true);

      uploadFile(current).then((url: string | string[]) => {
        setUploadedImages((prevFiles) => [url, ...prevFiles]);
        setFilesToUpload((prevFiles) => prevFiles.filter((f) => f !== current));
        setUploadedFilesRef((prevFiles) => [current, ...prevFiles]);
        setIsUploading(false);
      });
    }
  }, [filesToUpload]);

  const onDelete = (url: string) => {
    setUploadedImages((prevFiles) =>
      prevFiles.filter((item) => {
        if (Array.isArray(item)) {
          return item[0] !== url;
        }
        return item !== url;
      }),
    );
  };

  const uploadFile = async (file: File): Promise<string | string[]> => {
    const isVideo = file.type.startsWith(`video/`);
    if (isVideo) {
      const resp = await uploadGenericVideo(file);
      return resp.urls;
    }
    const resp = await uploadGenericImage(file);
    return resp.url;
  };

  const clearFiles = () => {
    setFilesToUpload([]);
    setUploadedImages([]);
    setUploadedFilesRef([]);
  };

  const filesList: FileTileModel[] = useMemo(() => {
    const out: FileTileModel[] = [];
    filesToUpload.forEach((file) => {
      out.push({
        url: URL.createObjectURL(file),
        isUploading: true,
        type: file.type,
        hideImage: file.type.startsWith(`video/`),
      });
    });

    uploadedImages.forEach((file) => {
      out.push({
        url: Array.isArray(file) ? file[0] : file,
        isUploading: false,
        type: `image/jpeg`,
      });
    });

    return out;
  }, [filesToUpload, uploadedImages]);

  const reachMaxFiles = useMemo(
    () => filesList.length === MAX_FILES,
    [filesList.length],
  );

  return {
    filesList,
    uploadedImages,
    onDelete,
    dropzoneProps,
    isUploading,
    reachMaxFiles,
    uploadedFilesRef,
    clearFiles,
    addFileToUploadQueue,
  };
}
